import React, { useEffect, useState } from 'react';

const PRESETS = [100, 250, 500];

const styles = {
    backdrop: {
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.36)',
        zIndex: 1200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
    },
    dialog: {
        width: 'min(460px, 100%)',
        background: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        boxShadow: '0 24px 70px rgba(15, 23, 42, 0.22)',
        padding: '22px',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
        marginBottom: '18px',
    },
    title: {
        margin: 0,
        fontSize: '18px',
        fontWeight: 800,
        color: '#0f172a',
    },
    close: {
        width: '32px',
        height: '32px',
        border: '1px solid #e2e8f0',
        borderRadius: '8px',
        background: '#ffffff',
        cursor: 'pointer',
        color: '#475569',
        fontSize: '18px',
        lineHeight: 1,
    },
    presetRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '10px',
        marginBottom: '14px',
    },
    preset: {
        height: '42px',
        border: '1px solid #dbe3ef',
        borderRadius: '8px',
        background: '#f8fafc',
        color: '#0f172a',
        fontSize: '13px',
        fontWeight: 700,
        cursor: 'pointer',
    },
    presetActive: {
        border: '1px solid var(--cux-ink, #132848)',
        background: '#f1f5f9',
        color: 'var(--cux-ink, #132848)',
    },
    label: {
        display: 'block',
        marginBottom: '8px',
        color: '#475569',
        fontSize: '12px',
        fontWeight: 700,
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
    },
    input: {
        width: '100%',
        height: '42px',
        border: '1px solid #dbe3ef',
        borderRadius: '8px',
        padding: '0 12px',
        fontSize: '14px',
        boxSizing: 'border-box',
        outline: 'none',
    },
    footer: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '10px',
        marginTop: '20px',
    },
    // Footer buttons mirror the app-wide button vocabulary: primary is the
    // navy .sxi_primary_blue_btn look, cancel the .back-button look (34px
    // tall, 6px radius) — inline because this modal renders standalone.
    cancel: {
        height: '34px',
        padding: '0 14px',
        border: '1px solid #d1d5db',
        borderRadius: '6px',
        background: '#f3f4f6',
        color: '#374151',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
    primary: {
        height: '34px',
        padding: '0 16px',
        border: '1px solid var(--cux-ink, #132848)',
        borderRadius: '6px',
        background: 'var(--cux-ink, #132848)',
        color: '#ffffff',
        fontSize: '14px',
        fontWeight: 500,
        cursor: 'pointer',
    },
};

export default function TopUpCreditsModal({
    open,
    defaultCredits = 100,
    loading = false,
    onClose,
    onSubmit,
}) {
    const [selectedCredits, setSelectedCredits] = useState(defaultCredits);

    useEffect(() => {
        if (open) setSelectedCredits(defaultCredits);
    }, [defaultCredits, open]);

    if (!open) return null;

    const submit = () => {
        const amount = Math.ceil(Number(selectedCredits));
        if (!Number.isFinite(amount) || amount <= 0) return;
        onSubmit?.(amount);
    };

    return (
        <div style={styles.backdrop} role="presentation">
            <div style={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="topup-credits-title">
                <div style={styles.header}>
                    <h2 id="topup-credits-title" style={styles.title}>Top up credits</h2>
                    <button type="button" style={styles.close} onClick={onClose} aria-label="Close" disabled={loading}>
                        ×
                    </button>
                </div>

                <div style={styles.presetRow}>
                    {PRESETS.map((amount) => (
                        <button
                            key={amount}
                            type="button"
                            style={{
                                ...styles.preset,
                                ...(Number(selectedCredits) === amount ? styles.presetActive : {}),
                            }}
                            onClick={() => setSelectedCredits(amount)}
                            disabled={loading}
                        >
                            {amount} credits
                        </button>
                    ))}
                </div>

                <label style={styles.label} htmlFor="topup-custom-credits">Custom amount</label>
                <input
                    id="topup-custom-credits"
                    style={styles.input}
                    type="number"
                    min="1"
                    step="1"
                    value={selectedCredits}
                    onChange={(e) => setSelectedCredits(e.target.value)}
                    disabled={loading}
                />

                <div style={styles.footer}>
                    <button type="button" style={styles.cancel} onClick={onClose} disabled={loading}>
                        Cancel
                    </button>
                    <button type="button" style={styles.primary} onClick={submit} disabled={loading}>
                        {loading ? 'Opening checkout' : 'Continue to Razorpay'}
                    </button>
                </div>
            </div>
        </div>
    );
}
